import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/shared/i18n/config';

interface SEOData {
  seoTitle: string;
  metaDescription: string;
  keywords: string;
}

const SEOHead: React.FC = () => {
  const { i18n: i18nInstance } = useTranslation();
  const location = useLocation();
  const language = (i18nInstance.language as 'fa' | 'en') || 'fa';
  const isRTL = language === 'fa';

  // SEO translations
  const currentSEO: SEOData = {
    seoTitle: i18nInstance.t('seo.title'),
    metaDescription: i18nInstance.t('seo.metaDescription'),
    keywords: language === 'fa' 
      ? 'کیوردیاب, تحقیق کلمات کلیدی, سئو, اینستاگرام, کلمات کلیدی'
      : 'keywordyab, keyword research, seo, instagram, keywords',
  };
  const currentURL = typeof window !== 'undefined' 
    ? `${window.location.origin}${location.pathname}`
    : '';

  // Update HTML lang and dir attributes
  useEffect(() => {
    document.documentElement.lang = language === 'fa' ? 'fa' : 'en';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    
    // Update meta description directly
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', currentSEO.metaDescription);
    } else {
      // Create meta description if it doesn't exist
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = currentSEO.metaDescription;
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
    
    // Update title directly
    document.title = currentSEO.seoTitle;
  }, [language, isRTL, currentSEO.metaDescription, currentSEO.seoTitle]);

  return (
    <Helmet>
      {/* Primary language (current) */}
      <html lang={language === 'fa' ? 'fa' : 'en'} dir={isRTL ? 'rtl' : 'ltr'} />
      
      {/* Title - This will be visible in source */}
      <title key={`title-${language}`}>{currentSEO.seoTitle}</title>
      
      {/* Meta Description - This will be visible in source */}
      <meta name="description" content={currentSEO.metaDescription} data-react-helmet="true" />
      
      {/* Keywords */}
      <meta name="keywords" content={currentSEO.keywords} key={`keywords-${language}`} />
      
      {/* Footer Description - Static meta tag for SEO */}
      <meta 
        name="footer-description" 
        content={i18nInstance.t('footer.description')} 
        key={`footer-desc-${language}`} 
      />
      
      {/* Alternate language meta tags */}
      <meta name="description" lang="fa" content={i18n.getResource('fa', 'translation', 'seo.metaDescription')} />
      <meta name="description" lang="en" content={i18n.getResource('en', 'translation', 'seo.metaDescription')} />
      
      {/* Alternate language links */}
      <link rel="alternate" hrefLang="fa" href={`${typeof window !== 'undefined' ? window.location.origin : ''}/`} />
      <link rel="alternate" hrefLang="en" href={`${typeof window !== 'undefined' ? window.location.origin : ''}/en`} />
      <link rel="alternate" hrefLang="x-default" href={`${typeof window !== 'undefined' ? window.location.origin : ''}/`} />
      
      {/* Open Graph - Visible in source */}
      <meta property="og:title" content={currentSEO.seoTitle} key={`og-title-${language}`} />
      <meta property="og:description" content={currentSEO.metaDescription} key={`og-description-${language}`} />
      <meta property="og:locale" content={language === 'fa' ? 'fa_IR' : 'en_US'} key={`og-locale-${language}`} />
      <meta property="og:locale:alternate" content={language === 'fa' ? 'en_US' : 'fa_IR'} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentURL} key={`og-url-${language}`} />
      
      {/* Twitter Card - Visible in source */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={currentSEO.seoTitle} key={`twitter-title-${language}`} />
      <meta name="twitter:description" content={currentSEO.metaDescription} key={`twitter-description-${language}`} />
      
      {/* Structured Data (JSON-LD) - Visible in source */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": i18nInstance.t('common.toolName'),
          "alternateName": language === 'fa' ? i18n.getResource('en', 'translation', 'common.toolName') : i18n.getResource('fa', 'translation', 'common.toolName'),
          "description": currentSEO.metaDescription,
          "applicationCategory": "SEO Tool",
          "operatingSystem": "Web",
          "url": currentURL,
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "inLanguage": [language === 'fa' ? 'fa-IR' : 'en-US', language === 'fa' ? 'en-US' : 'fa-IR']
        })}
      </script>

      {/* FAQPage Structured Data (JSON-LD) - Persian - Static - Visible in source */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "inLanguage": "fa-IR",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "چطور رایگان استفاده کنیم؟",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "کافی هست ثبت‌نام کنید و یک هفته رایگان استفاده کنید، آیا میشه همیشه رایگان استفاده کرد؟ بله سوال بعدی را مطالعه کنید."
              }
            },
            {
              "@type": "Question",
              "name": "چطور میتوان برای همیشه رایگان استفاده کنیم؟",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "به ازای هر نفری که از سمت کد معرف شما عضو سایت کیوردیاب بشن یک هفته به سفارش شما اضافه میشه."
              }
            },
            {
              "@type": "Question",
              "name": "آیا بدون کد معرف میتونیم عضو بشیم؟",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "خیر، برای عضویت و استفاده از ابزار حتما باید کد معرف از شخصی دریافت کنید و اگر ندارید در گوگل یا لینکدین کد معرف کیوردیاب را جستجو کنید."
              }
            }
          ]
        })}
      </script>

      {/* FAQPage Structured Data (JSON-LD) - English - Static - Visible in source */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "inLanguage": "en-US",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How to use for free?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Just sign up and use it for free for one week. Can we use it for free forever? Yes, please read the next question."
              }
            },
            {
              "@type": "Question",
              "name": "How can we use it for free forever?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "For every person who joins Keywordyab using your referral code, one week will be added to your subscription."
              }
            },
            {
              "@type": "Question",
              "name": "Can we join without a referral code?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No, to join and use the tool, you must receive a referral code from someone. If you don't have one, search for \"Keywordyab referral code\" on Google or LinkedIn."
              }
            }
          ]
        })}
      </script>
      
    </Helmet>
  );
};

export default SEOHead;
